// Bridges the legacy monolithic course components (which write their own
// {completed} maps into localStorage under flexx_v5, flexx_adv_v5, ...) to
// the new FLEXX v3 XP system. On every poll tick we diff the completion
// count against the last known value and award +50 XP per newly-completed
// lesson. Also pings the streak so the Overview dashboard stays accurate.
//
// This is intentionally zero-touch for the course JSX files — per user's
// explicit instruction to keep those 2000-line monoliths as-is.

import { flexxState, awardXP } from "./state";

const COURSE_KEYS = [
  { id: "beginner",    storageKey: "flexx_v5",     label: "Beginner" },
  { id: "advanced",    storageKey: "flexx_adv_v5", label: "Advanced" },
  { id: "cfd",         storageKey: "flexx_cfd_v5", label: "CFD" },
  { id: "commodities", storageKey: "flexx_com_v5", label: "Commodities" },
  { id: "roadmap",     storageKey: "flexx_rm_v5",  label: "Roadmap" },
];

const BASELINE_KEY = "flexx_course_xp_baseline"; // last known completion counts per course
const POLL_MS = 2500;
const XP_PER_LESSON = 50;

function readCompletionCount(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return 0;
    const parsed = JSON.parse(raw);
    return Object.values(parsed?.completed || {}).filter(Boolean).length;
  } catch {
    return 0;
  }
}

function readBaseline() {
  try {
    return JSON.parse(localStorage.getItem(BASELINE_KEY) || "{}");
  } catch {
    return {};
  }
}

function writeBaseline(b) {
  try { localStorage.setItem(BASELINE_KEY, JSON.stringify(b)); } catch {}
}

let pollTimer = null;
let running = false;

function tick() {
  const baseline = readBaseline();
  let changed = false;
  let totalNew = 0;
  COURSE_KEYS.forEach(({ id, storageKey, label }) => {
    const current = readCompletionCount(storageKey);
    const prev = baseline[id] ?? current; // first time we see this key, treat current as baseline (don't award retroactively)
    if (current > prev) {
      const delta = current - prev;
      totalNew += delta;
      awardXP(delta * XP_PER_LESSON, `${label} lessons (+${delta})`);
    }
    if (baseline[id] !== current) {
      baseline[id] = current;
      changed = true;
    }
  });
  if (changed) {
    writeBaseline(baseline);
    window.dispatchEvent(new CustomEvent("flexx:course-progress", { detail: { totalNew } }));
    // Trigger a cloud-sync push so course completion is persisted server-side
    // (these keys aren't written via state.set, so we must announce the change).
    window.dispatchEvent(new CustomEvent("flexx:mutation", { detail: { key: "courses" } }));
    // Also ping the streak so the user gets daily-activity credit from
    // doing actual course lessons (not just opening the dashboard).
    if (totalNew > 0) flexxState.pingStreak();
  }
}

export function startCourseXPBridge() {
  if (running) return;
  running = true;
  // Seed baseline on first run so existing completed lessons don't dump XP
  const baseline = readBaseline();
  COURSE_KEYS.forEach(({ id, storageKey }) => {
    if (baseline[id] == null) baseline[id] = readCompletionCount(storageKey);
  });
  writeBaseline(baseline);
  // Poll tick
  pollTimer = setInterval(tick, POLL_MS);
}

export function stopCourseXPBridge() {
  running = false;
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
}

/**
 * On logout / new device re-login, reset the baseline to match the current
 * localStorage so the next login doesn't award historical XP deltas.
 */
export function resetCourseXPBridgeBaseline() {
  const baseline = {};
  COURSE_KEYS.forEach(({ id, storageKey }) => {
    baseline[id] = readCompletionCount(storageKey);
  });
  writeBaseline(baseline);
}
